import { useId, useRef, useState, type ReactNode } from 'react';
import { ArrowLeft, CaretRight } from '@phosphor-icons/react';
import type { CodeCategoryNode } from '@longjie/shared';
import { CategoryImage } from './CategoryImage';
import { SearchClearButton } from './SearchClearButton';
import { categoryLevelStyle } from './category-level-style';
import './category-navigation.css';

/** The desktop category data, navigated one full-width level at a time. */
export function MobileCategoryPicker({ nodes, legacyCategories, selectedValue, onSelect, compact = false, showAllProducts = false, onShowAllProducts, showLegacyCategories = false, headingAction }: {
  nodes: CodeCategoryNode[];
  legacyCategories: Array<{ id: string; name: string; productCount?: number; active?: boolean }>;
  selectedValue: string;
  onSelect: (value: string) => void;
  compact?: boolean;
  showAllProducts?: boolean;
  onShowAllProducts?: () => void;
  showLegacyCategories?: boolean;
  headingAction?: ReactNode;
}) {
  const [query, setQuery] = useState('');
  const searchId = useId();
  const searchRef = useRef<HTMLInputElement>(null);
  const [allExpanded, setAllExpanded] = useState(false);
  const selected = nodes.find(node => `code:${node.prefix}` === selectedValue);
  const selectedLegacy = legacyCategories.find(category => category.id === selectedValue);
  const children = nodes.filter(node => node.parentPrefix === (selected?.prefix ?? null));
  const siblings = selected ? nodes.filter(node => node.parentPrefix === selected.parentPrefix) : [];
  const ancestors = selected ? nodes.filter(node => selected.prefix.startsWith(node.prefix) && node.prefix !== selected.prefix) : [];
  const search = query.trim().toLocaleLowerCase('zh-CN');
  const visible = search ? nodes.filter(node => `${node.prefix} ${node.name ?? ''} ${node.label} ${node.path}`.toLocaleLowerCase('zh-CN').includes(search))
    : compact ? [] : allExpanded && !selected ? [...nodes].sort((a, b) => a.prefix.localeCompare(b.prefix, 'en')) : children;
  const childCounts = new Map<string, number>();
  for (const node of nodes) if (node.parentPrefix) childCounts.set(node.parentPrefix, (childCounts.get(node.parentPrefix) ?? 0) + 1);
  function select(value: string) { setQuery(''); setAllExpanded(false); onSelect(value); }
  function expandAll(expanded: boolean) { setQuery(''); setAllExpanded(expanded); onSelect('all'); }

  return <section className="mobile-category-picker" aria-label="逐级商品分类">
    {selected && <nav className="mobile-category-picker__path" aria-label="分类路径">
      <button type="button" onClick={() => select('all')}>全部分类</button>
      {ancestors.map(node => <button key={node.prefix} type="button" onClick={() => select(`code:${node.prefix}`)}>
        <CaretRight aria-hidden="true" />{node.name || node.label}
      </button>)}
    </nav>}
    <div className={`mobile-category-picker__heading${selected && headingAction ? ' mobile-category-picker__heading--managed' : ''}`}>
      {selected && <button type="button" className="mobile-category-picker__back" aria-label="返回上一级分类"
        onClick={() => select(selected.parentPrefix ? `code:${selected.parentPrefix}` : 'all')}><ArrowLeft />上一级</button>}
      <div><h2>{selected?.name || selected?.label || selectedLegacy?.name || (showAllProducts ? '全部商品' : '商品目录')}</h2>
        <p>{selected ? `分类 ${selected.prefix}` : showAllProducts ? `共 ${children.reduce((total, node) => total + node.productCount, 0)} 种商品` : `${children.length} 个一级分类`}{children.length > 0 && selected ? ` · ${children.length} 个下级分类` : ''}</p></div>
      {headingAction && <span className="mobile-category-picker__heading-actions">{headingAction}</span>}
    </div>
    {((selected && siblings.length > 1) || (children.length > 0 && (!compact || !selected))) && <div className="mobile-category-picker__tools">
      {selected && siblings.length > 1 && <label className="mobile-category-picker__siblings"><span>同级</span>
        <select value={selected.prefix} aria-label="切换同级分类" onChange={event => select(`code:${event.target.value}`)}>
          {siblings.map(node => <option key={node.prefix} value={node.prefix}>{node.prefix} · {node.name || node.label}</option>)}
        </select>
      </label>}
      {children.length > 0 && (!compact || !selected) && <div className="mobile-category-picker__search"><label htmlFor={searchId} className={selected ? 'sr-only' : undefined}>查找分类</label>
        <div className="mobile-category-picker__search-field">
          <input id={searchId} ref={searchRef} className="search-clearable" type="search" value={query} onChange={event => { if (compact) onSelect('all'); setQuery(event.target.value); }} placeholder="分类编码或分类名称" />
          {query !== '' && <SearchClearButton label="清除分类搜索" onClear={() => { setQuery(''); searchRef.current?.focus({ preventScroll: true }); }} />}
        </div>
      </div>}
    </div>}
    {!selected && <div className="mobile-category-picker__actions" role="group" aria-label="商品分类操作">
      <button type="button" aria-pressed={showAllProducts} onClick={() => { setQuery(''); setAllExpanded(false); if (onShowAllProducts) onShowAllProducts(); else onSelect('all'); }}>全部商品</button>
      <button type="button" aria-pressed={allExpanded && !compact} onClick={() => expandAll(true)}>展开全部</button>
      <button type="button" onClick={() => expandAll(false)}>收起全部</button>
    </div>}
    {(visible.length > 0 || search) && <div className="mobile-category-picker__list">
      {visible.map(node => <button type="button" className="mobile-category-picker__row" key={node.prefix} data-depth={node.depth} style={categoryLevelStyle(node.depth)}
        onClick={() => select(`code:${node.prefix}`)} aria-label={`打开分类 ${node.prefix} ${node.name || node.label}`}>
        <span className="mobile-category-picker__image" aria-hidden="true"><CategoryImage prefix={node.prefix} imageUrl={node.imageUrl} alt="" /></span>
        <span className="mobile-category-picker__copy">
          <span className="code-category-tree__title">
            <span className="code-category-tree__level" aria-label={`第 ${node.depth} 级分类`}>{node.depth === 1 ? `${node.segment} 大类` : `${node.depth}级`}</span>
            <strong>{node.name || node.label}</strong>
          </span>
          <span className="mobile-category-picker__details">
          {node.depth > 1 && <span className="code-category-tree__path" aria-label={`分类路径 ${node.path}`}>
            {node.prefix.match(/.{2}/g)?.map((segment, index, segments) => <span className="code-category-tree__path-part" style={categoryLevelStyle(index + 1)} key={index}>
              {index > 0 && <span className="code-category-tree__path-separator" aria-hidden="true">/</span>}
              <span className={index === segments.length - 1 ? 'code-category-tree__path-current' : undefined}>{segment}</span>
            </span>)}
          </span>}
          <span className="code-category-tree__counts"><span><b>{node.productCount}</b> 种商品</span>
            {(childCounts.get(node.prefix) ?? 0) > 0 && <span className="code-category-tree__descendants" title="商品数量包含全部下级分类">含下级</span>}
          </span>
          </span>
        </span><CaretRight aria-hidden="true" />
      </button>)}
      {search && visible.length === 0 && <p role="status">没有匹配的分类，请换个名称或编号。</p>}
    </div>}
    {!compact && nodes.length === 0 && legacyCategories.length > 0 && <div className="mobile-category-picker__list">
      {legacyCategories.map(category => <button key={category.id} type="button" className="mobile-category-picker__row" onClick={() => select(category.id)}>
        <span className="mobile-category-picker__image" aria-hidden="true" />
        <span className="mobile-category-picker__copy"><b>{category.name}{category.active === false ? '（已停用）' : ''}</b>{category.productCount !== undefined && <small>{category.productCount} 种商品</small>}</span><CaretRight />
      </button>)}
    </div>}
    {showLegacyCategories && !compact && !selected && nodes.length > 0 && legacyCategories.length > 0 && <details className="code-category-legacy" open={Boolean(selectedLegacy)}>
      <summary>按原分类名称分组筛选</summary>
      <div className="product-category-legacy" role="group" aria-label="原分类名称分组">
        {legacyCategories.map(category => <button type="button" key={category.id} className="category-navigation-all" aria-pressed={selectedValue === category.id} onClick={() => select(category.id)}>
          {category.name}{category.productCount === undefined ? '' : `（${category.productCount}）`}{category.active === false ? '（已停用）' : ''}
        </button>)}
      </div>
    </details>}
  </section>;
}
